import type { AxiosInstance } from "axios";
import { AUTH_SESSION_KEY } from "@/lib/auth-helpers";
import type { RefreshActivity } from "./refresh-activity";
import { browserRefreshMutex, type RefreshMutex } from "./refresh-mutex";

interface RefreshResponse {
  accessToken: string;
  refreshToken: string;
}

export class SessionCoordinator {
  private refreshPromise: Promise<string> | null = null;
  private sessionVersion = 0;
  private knownRefreshToken: string | null;
  private knownSessionId: string | null;

  constructor(
    private readonly storage: Storage,
    private readonly refreshTransport: AxiosInstance,
    private readonly activity?: RefreshActivity,
    private readonly mutex: RefreshMutex = browserRefreshMutex,
  ) {
    this.knownRefreshToken = storage.getItem("refresh_token");
    this.knownSessionId = storage.getItem(AUTH_SESSION_KEY);
  }

  getAccessToken(): string | null {
    return this.storage.getItem("access_token");
  }

  getRefreshToken(): string | null {
    return this.storage.getItem("refresh_token");
  }

  getSessionVersion(): number {
    const current = this.getRefreshToken();
    const sessionId = this.storage.getItem(AUTH_SESSION_KEY);
    if (sessionId !== this.knownSessionId || (!sessionId && current !== this.knownRefreshToken)) {
      this.sessionVersion += 1;
    }
    this.knownSessionId = sessionId;
    this.knownRefreshToken = current;
    return this.sessionVersion;
  }

  isSameSession(version: number | undefined): boolean {
    return version === this.getSessionVersion();
  }

  refresh(): Promise<string> {
    if (this.refreshPromise) return this.refreshPromise;
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) return Promise.reject(new Error("Refresh token ausente."));
    const sessionId = this.storage.getItem(AUTH_SESSION_KEY);

    const finishActivity = this.activity?.begin();
    this.refreshPromise = this.mutex
      .run(async () => {
        if (this.storage.getItem(AUTH_SESSION_KEY) !== sessionId) {
          throw new Error("Sessão alterada durante a renovação.");
        }
        if (this.getRefreshToken() !== refreshToken) {
          const accessToken = this.getAccessToken();
          if (!sessionId || !accessToken) {
            throw new Error("Sessão alterada durante a renovação.");
          }
          this.knownRefreshToken = this.getRefreshToken();
          return accessToken;
        }
        let data: RefreshResponse;
        try {
          ({ data } = await this.refreshTransport.post<RefreshResponse>("/api/v1/auth/refresh", {
            refreshToken,
          }));
        } catch (error) {
          const rotatedToken = this.getRefreshToken();
          const accessToken = this.getAccessToken();
          if (
            sessionId &&
            this.storage.getItem(AUTH_SESSION_KEY) === sessionId &&
            rotatedToken &&
            rotatedToken !== refreshToken &&
            accessToken
          ) {
            this.knownRefreshToken = rotatedToken;
            return accessToken;
          }
          throw error;
        }
        if (!data.accessToken || !data.refreshToken) {
          throw new Error("Resposta de renovação inválida.");
        }
        if (
          this.storage.getItem(AUTH_SESSION_KEY) !== sessionId ||
          this.getRefreshToken() !== refreshToken
        ) {
          throw new Error("Sessão alterada durante a renovação.");
        }
        this.storage.setItem("access_token", data.accessToken);
        this.storage.setItem("refresh_token", data.refreshToken);
        this.knownRefreshToken = data.refreshToken;
        return data.accessToken;
      })
      .finally(() => {
        this.refreshPromise = null;
        finishActivity?.();
      });
    return this.refreshPromise;
  }
}
